import { Icon } from "@legal-500/legal500-design-system";
import clsx from "clsx";
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type FocusEvent as ReactFocusEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { GlobalSearchItem, GlobalSearchSection } from "../../api/search.service";
import styles from "./GlobalSearch.module.css";
import { HighlightMatch } from "./HighlightMatch";
import {
  isKnownSectionKey,
  MAX_ITEMS_PER_SECTION,
  MIN_QUERY_LENGTH,
  SECTION_CONFIG,
} from "./searchSections";
import { useGlobalSearch } from "./useGlobalSearch";

type FlatOption = {
  id: string;
  href: string;
  item: GlobalSearchItem;
};

type VisibleSection = {
  section: GlobalSearchSection;
  headingId: string;
  options: FlatOption[];
  viewAllHref?: string;
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}

/** Staff-wide search bar with a sectioned results panel (combobox + listbox). */
export function GlobalSearch() {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const statusId = `${baseId}-status`;

  const navigate = useNavigate();
  const location = useLocation();

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { data, isError, isPlaceholderData, isSearching, debouncedQuery } = useGlobalSearch(value);

  const trimmed = value.trim();
  const isQueryLongEnough = trimmed.length >= MIN_QUERY_LENGTH;

  const sections = useMemo<VisibleSection[]>(() => {
    if (!data) return [];
    const resultsQuery = data.query || debouncedQuery;
    return data.sections
      .filter(section => isKnownSectionKey(section.key) && section.items.length > 0)
      .map(section => {
        const config = SECTION_CONFIG[section.key];
        return {
          section,
          headingId: `${baseId}-section-${section.key}`,
          viewAllHref: config.viewAllHref?.(resultsQuery),
          options: section.items.slice(0, MAX_ITEMS_PER_SECTION).map((item, index) => ({
            id: `${baseId}-option-${section.key}-${index}`,
            href: config.itemHref(item),
            item,
          })),
        };
      });
  }, [data, baseId, debouncedQuery]);

  const options = useMemo(() => sections.flatMap(section => section.options), [sections]);
  const unavailableSources = useMemo(
    () =>
      (data?.errors ?? []).map(error =>
        isKnownSectionKey(error.key) ? SECTION_CONFIG[error.key].label : error.key,
      ),
    [data],
  );

  // A fresh result set invalidates the highlighted row.
  useEffect(() => {
    setActiveIndex(-1);
  }, [options]);

  // Close whenever the route changes (result click, Enter, back button...).
  useEffect(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, [location.pathname, location.search]);

  // Global shortcuts: Cmd/Ctrl+K anywhere, "/" when not already typing.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isModK =
        (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k";
      const isSlash =
        event.key === "/" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isEditableTarget(event.target);

      if (!isModK && !isSlash) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close on clicks outside the search.
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  // Keep the highlighted option visible when arrowing through a long panel.
  useEffect(() => {
    if (activeIndex < 0) return;
    const element = document.getElementById(options[activeIndex]?.id ?? "");
    element?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, options]);

  const isExpanded = isOpen && isQueryLongEnough;
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    setIsOpen(true);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!isExpanded) {
          setIsOpen(true);
          return;
        }
        if (options.length === 0) return;
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActiveIndex(current => {
          if (current < 0) return step === 1 ? 0 : options.length - 1;
          return (current + step + options.length) % options.length;
        });
        return;
      }
      case "Enter": {
        event.preventDefault();
        if (isExpanded && activeOption) {
          navigate(activeOption.href);
          setIsOpen(false);
        }
        return;
      }
      case "Escape": {
        event.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
        return;
      }
      default:
    }
  }

  function handleBlur(event: ReactFocusEvent<HTMLDivElement>) {
    // Tabbing out of the whole widget closes it; moving within it does not.
    const next = event.relatedTarget as Node | null;
    if (next && containerRef.current?.contains(next)) return;
    if (next) setIsOpen(false);
  }

  function clear() {
    setValue("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  // Before the first response for this query arrives, show a loading state
  // instead of the previous query's "no results".
  const hasFreshData = !!data && !isPlaceholderData;
  const showEmpty = hasFreshData && !isSearching && sections.length === 0 && !isError;
  const showInitialLoading = sections.length === 0 && !showEmpty && !isError && isSearching;

  let statusMessage = "";
  if (isExpanded) {
    if (isError) statusMessage = "Search is unavailable right now.";
    else if (showEmpty) statusMessage = `No results for '${trimmed}'.`;
    else if (showInitialLoading) statusMessage = "Searching…";
    else if (!isSearching)
      statusMessage = `${options.length} result${options.length === 1 ? "" : "s"} available.`;
  }

  const shortcutHint = isMacPlatform() ? "⌘K" : "Ctrl K";

  return (
    <div className={styles.globalSearch} ref={containerRef} onBlur={handleBlur}>
      <form
        role="search"
        aria-label="Global search"
        className={styles.searchForm}
        onSubmit={event => event.preventDefault()}
      >
        <span className={styles.searchIcon} aria-hidden="true">
          <Icon icon="magnifier" size="small" />
        </span>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          className={styles.input}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder="Search companies, contacts, profiles, articles, users…"
          aria-label="Search the staff platform"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={isExpanded && sections.length > 0 ? listboxId : undefined}
          aria-activedescendant={isExpanded && activeOption ? activeOption.id : undefined}
          aria-keyshortcuts="Meta+K Control+K /"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
        />
        {isExpanded && isSearching && (
          <span className={styles.spinner} data-testid="global-search-loading" aria-hidden="true" />
        )}
        {value ? (
          <button
            type="button"
            className={styles.clearButton}
            onClick={clear}
            aria-label="Clear search"
          >
            <Icon icon="cross" size="small" />
          </button>
        ) : (
          <kbd className={styles.shortcut} aria-hidden="true">
            {shortcutHint}
          </kbd>
        )}
      </form>

      <span id={statusId} className={styles.srOnly} role="status" aria-live="polite">
        {statusMessage}
      </span>

      <div className={clsx(styles.panel, !isExpanded && styles.panelHidden)}>
        {isExpanded && unavailableSources.length > 0 && (
          <p className={styles.notice}>
            Some sources are unavailable right now: {unavailableSources.join(", ")}.
          </p>
        )}

        {isExpanded && isError && (
          <p className={styles.message}>Search is unavailable right now. Please try again.</p>
        )}

        {isExpanded && showInitialLoading && (
          <p className={styles.message}>
            <span className={styles.spinner} aria-hidden="true" />
            Searching…
          </p>
        )}

        {isExpanded && showEmpty && (
          <p className={styles.message}>No results for &lsquo;{trimmed}&rsquo;</p>
        )}

        {isExpanded && sections.length > 0 && (
          <div id={listboxId} role="listbox" aria-label="Search results" className={styles.grid}>
            {sections.map(({ section, headingId, options: sectionOptions, viewAllHref }) => (
              <div
                key={section.key}
                role="group"
                aria-labelledby={headingId}
                className={styles.section}
                data-testid={`global-search-section-${section.key}`}
              >
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionIcon} aria-hidden="true">
                    <Icon icon={SECTION_CONFIG[section.key].icon} size="small" />
                  </span>
                  <span id={headingId} className={styles.sectionLabel}>
                    {section.label}
                  </span>
                  <span className={styles.sectionCount} aria-hidden="true">
                    {sectionOptions.length}
                  </span>
                  {viewAllHref && (
                    <Link
                      to={viewAllHref}
                      className={styles.viewAll}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => setIsOpen(false)}
                      aria-label={`View all ${section.label}`}
                    >
                      View all
                    </Link>
                  )}
                </div>
                <ul className={styles.options} role="presentation">
                  {sectionOptions.map(option => {
                    const isActive = activeOption?.id === option.id;
                    return (
                      <li key={option.id} role="presentation">
                        <Link
                          id={option.id}
                          to={option.href}
                          role="option"
                          aria-selected={isActive}
                          tabIndex={-1}
                          className={clsx(styles.option, isActive && styles.optionActive)}
                          onMouseDown={event => event.preventDefault()}
                          onMouseEnter={() => setActiveIndex(options.indexOf(option))}
                          onClick={() => setIsOpen(false)}
                        >
                          <span className={styles.optionTitle}>
                            <HighlightMatch text={option.item.title} query={trimmed} />
                          </span>
                          {option.item.subtitle && (
                            <span className={styles.optionSubtitle}>{option.item.subtitle}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
